import React, { useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { Dialog } from "@mui/material";
import { Avatar } from "../avatar";
import { FormField, FormSelectField } from "../form-fields";
import { SectionHeader } from "./SectionHeader";
import { AddAnotherButton } from "./AddAnotherButton";
import { LeadFormComponentProps, LeadFormState } from "../lead-form/definitions/types";
import { validate } from "shared/yup";
import moment from "moment";
import { YesNoType } from "type/boolean-choice-type";
import { LeadReadiness } from "type/lead-readiness";
import { LeadTimelineType } from "type/lead-timeline";
import { formatPriceInput } from "shared/price-input-formatter";
import { Select } from "../select";
import { LeadTagType } from "type/lead-tag";
import { LeadAnniversary } from "model/lead-anniversary";
import { LeadAnniversaryType } from "type/lead-anniversary";
import { LeadPhoneType } from "type/lead-phone";
import { AppDatePicker, toDateValue } from "component/shared/date-picker";
import { styles } from "./style";
import * as env from "shared/env";
import * as Router from "react-router-dom";
import { UnsavedChangesDialog, UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";

const initialFormState: LeadFormState = {
	name: "",
	email: "",
	email2: "",
	phone: "",
	leadPhones: [],
	address: "",
	city: "",
	state: "",
	zip: "",
	agentId: 0,
	readiness: undefined,
	birthday: null,
	transactionAnniversary: null,
	typeIds: [],
	timeline: undefined,
	transactionTimelineStart: undefined,
	transactionTimelineEnd: undefined,
	justBrowsing: false,
	transactionPrice: undefined,
	workingWithAnotherAgent: undefined,
	mortgagePreApproval: undefined,
	referralRequested: undefined,
	referralRequestedFlag: undefined,
};

type LeadFormV2Props = Omit<LeadFormComponentProps, "classes"> & WithStyles<typeof styles>;

const LeadFormV2Component = ({
	onClose,
	open,
	isEdit,
	submitted,
	leadPhonesList,
	crmPhones,
	crmAnniversary,
	crmTags,
	canAddPhone,
	lead,
	isBroker,
	isAdmin,
	agents,
	handleSubmit,
	classes = {},
	leadAnniversaries,
	typeTags,
	validationSchema,
	defaultAgentId,
	extendedLeadFields,
}: LeadFormV2Props) => {
	const [state, setState] = useState<LeadFormState>(initialFormState);
	const [addEmail2, setAddEmail2] = useState(false);
	const [dirty, setDirty] = useState(false);
	const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
	// Add Lead navigates to the new lead before closing the form; don't block that navigation.
	// Validity is captured at submit because saving changes the schema (the email becomes a duplicate).
	const submittedRef = useRef(false);
	const history = Router.useHistory();

	const updateState = (nextState: LeadFormState) => {
		setState(nextState);
		setDirty(true);
		submittedRef.current = false;
	};

	const discardAndClose = () => {
		setDiscardDialogOpen(false);
		setDirty(false);
		setState(initialFormState);
		setAddEmail2(false);
		onClose();
	};

	const handleClose = () => {
		if (dirty) {
			setDiscardDialogOpen(true);
		} else {
			discardAndClose();
		}
	};

	const errors = validate(validationSchema, state);

	useEffect(() => {
		// The parent's selectors return new arrays on every store update, so this runs whenever
		// anything on the page dispatches. Reloading then would discard what the user has typed.
		// Closing the form runs it again with `open` false, which resets it for the next open.
		if (open && dirty) {
			return;
		}
		if (isEdit && lead) {
			// Load lead data for editing
			const {
				name,
				email,
				email2,
				phone,
				address,
				city,
				state: leadState,
				zip,
				agent,
				readiness,
				transactionPrice,
				workingWithAnotherAgent,
				mortgagePreApproval,
				referralRequested,
			} = lead;

			const birthday = leadAnniversaries?.find(
				(a) => a.type === LeadAnniversaryType.BIRTHDAY
			) || null;

			const transactionAnniversary = leadAnniversaries?.find(
				(a) => a.type === LeadAnniversaryType.TRANSACTION_ANNIVERSARY
			) || null;

			const typeIds = typeTags?.map((tag) => tag.text) || [];

			setState({
				...state,
				name: name || "",
				email: email || "",
				email2: email2 || "",
				phone: phone || "",
				leadPhones: leadPhonesList || [],
				address: address || "",
				city: city || "",
				state: leadState || "",
				zip: zip || "",
				agentId: agent?.id || 0,
				readiness,
				birthday,
				transactionAnniversary,
				typeIds,
				transactionPrice,
				workingWithAnotherAgent,
				mortgagePreApproval,
				referralRequested,
				referralRequestedFlag: !!referralRequested,
			});

			if (email2) {
				setAddEmail2(true);
			} else {
				setAddEmail2(false);
			}
		} else {
			// Reset for new lead
			setState({
				...initialFormState,
				agentId: defaultAgentId || 0,
			});
			setAddEmail2(false);
		}
		setDirty(false);
		submittedRef.current = false;
	}, [isEdit, lead, leadAnniversaries, typeTags, defaultAgentId, open, leadPhonesList]);

	const onStateUpdate = (field: string, value: any) => {
		updateState({ ...state, [field]: value });
	};

	const onChangeTimeline = (option: number | undefined) => {
		if (option === undefined || option === null) {
			updateState({
				...state,
				timeline: undefined,
				transactionTimelineStart: undefined,
				transactionTimelineEnd: undefined,
				justBrowsing: undefined,
			});
			return;
		}
		if (option === LeadTimelineType.JUST_BROWSING.value) {
			updateState({
				...state,
				timeline: LeadTimelineType.JUST_BROWSING.value,
				transactionTimelineStart: undefined,
				transactionTimelineEnd: undefined,
				justBrowsing: true,
			});
		} else {
			const now = moment();
			const end = moment(now).add(option, "days");
			const format = "YYYY-MM-DDTHH:mm:ss.SSSZ";
			updateState({
				...state,
				timeline: option,
				transactionTimelineStart: now.format(format),
				transactionTimelineEnd: end.format(format),
				justBrowsing: false,
			});
		}
	};

	const onReferralRequestedChange = (option: boolean | undefined) => {
		if (option === undefined || option === null) {
			updateState({
				...state,
				referralRequested: undefined,
				referralRequestedFlag: undefined,
			});
			return;
		}
		if (option) {
			updateState({
				...state,
				referralRequested: moment().format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
				referralRequestedFlag: true,
			});
		} else {
			updateState({
				...state,
				referralRequested: "",
				referralRequestedFlag: false,
			});
		}
	};

	const onChangeBirthday = (date: Date | null) => {
		if (isEdit) {
			let birthday = { ...state.birthday } as LeadAnniversary;
			if (!birthday || !birthday.id) {
				birthday = {
					...birthday,
					leadId: lead && lead.id,
					type: LeadAnniversaryType.getById(1),
					updatedOn: moment(),
				} as LeadAnniversary;
			}
			birthday.startDate = moment(date!);
			updateState({ ...state, birthday });
		} else {
			if (date !== null) {
				updateState({ ...state, birthday: date });
			}
		}
	};

	const onChangeTransactionAnniversary = (date: Date | null) => {
		if (isEdit) {
			let transactionAnniversary = { ...state.transactionAnniversary } as LeadAnniversary;
			if (!transactionAnniversary || !transactionAnniversary.id) {
				transactionAnniversary = {
					...transactionAnniversary,
					leadId: lead && lead.id,
					type: LeadAnniversaryType.getById(2),
					updatedOn: moment(),
				} as LeadAnniversary;
			}
			transactionAnniversary.startDate = moment(date!);
			updateState({ ...state, transactionAnniversary });
		} else {
			if (date !== null) {
				updateState({ ...state, transactionAnniversary: date });
			}
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			scroll="paper"
			maxWidth="sm"
			fullWidth
			PaperProps={{
				className: classes.dialogPaper,
			}}
		>
			<Mui.DialogTitle>
				<Mui.Grid container justifyContent="space-between" alignItems="center">
					<Mui.Grid item>
						<Mui.Typography component="h2" variant="h4">
							{isEdit ? "Edit Lead" : "Add Lead"}
						</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.IconButton size="small" onClick={handleClose}>
							<Icons.X size={20} />
						</Mui.IconButton>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.DialogTitle>
			<Mui.DialogContent dividers className={classes.dialogContent}>
				<Mui.Grid container direction="column" spacing={3}>
					{/* General Section */}
					<Mui.Grid item>
						<SectionHeader title="General" />
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.Grid container direction="column" spacing={3}>
							{/* Name */}
							<Mui.Grid item>
								<FormField
									label="Name"
									value={state.name}
									onChange={(e) => onStateUpdate("name", e.target.value)}
								/>
							</Mui.Grid>

							{/* Email */}
							<Mui.Grid item>
								<Mui.Grid container direction="column" spacing={1}>
									<Mui.Grid item>
										<FormField
											label="Email"
											value={state.email}
											onChange={(e) => onStateUpdate("email", e.target.value)}
											required
											error={submitted && !!errors && !!errors.email}
											helperText={(submitted && errors && errors.email) || undefined}
											type="email"
										/>
									</Mui.Grid>
									{!addEmail2 && (
										<Mui.Grid item>
											<AddAnotherButton onClick={() => setAddEmail2(true)} />
										</Mui.Grid>
									)}
									{addEmail2 && (
										<>
											<Mui.Grid item>
												<Mui.Grid container spacing={1} alignItems="center">
													<Mui.Grid item xs={11}>
														<FormField
															label="Email"
															value={state.email2}
															onChange={(e) => onStateUpdate("email2", e.target.value)}
															type="email"
															error={submitted && !!errors && !!errors.email2}
															helperText={(submitted && errors && errors.email2) || undefined}
														/>
													</Mui.Grid>
													<Mui.Grid item xs={1}>
														<Mui.IconButton
															size="small"
															onClick={() => {
																setAddEmail2(false);
																onStateUpdate("email2", "");
															}}
														>
															<Icons.Trash2 size={20} color="#ef4444" />
														</Mui.IconButton>
													</Mui.Grid>
												</Mui.Grid>
											</Mui.Grid>
										</>
									)}
								</Mui.Grid>
							</Mui.Grid>

							{/* Phone Number */}
							<Mui.Grid item>
								<Mui.Grid container direction="column" spacing={1}>
									<Mui.Grid item>
										<FormField
											label="Phone Number"
											value={state.phone}
											onChange={(e) => onStateUpdate("phone", e.target.value)}
											type="tel"
										/>
									</Mui.Grid>
									{ state.leadPhones
										.filter((phone) => !phone.deleted)
										.map((phone, index) => {
												const actualIndex = state.leadPhones.findIndex((p) => p === phone);
												return (
													<Mui.Grid item key={actualIndex}>
														<Mui.Grid container spacing={1} alignItems="center">
															<Mui.Grid item xs={6}>
																<FormField
																	label="Phone Number"
																	value={phone.value || ""}
																	onChange={(e) => {
																		const leadPhones = [...state.leadPhones];
																		leadPhones[actualIndex].value = e.target.value;
																		onStateUpdate("leadPhones", leadPhones);
																	}}
																	type="tel"
																/>
															</Mui.Grid>
															<Mui.Grid item xs={5}>
																<FormSelectField
																	label="Contact Type"
																	value={phone.type}
																	onChange={(value) => {
																		const leadPhones = [...state.leadPhones];
																		leadPhones[actualIndex].type = value;
																		onStateUpdate("leadPhones", leadPhones);
																	}}
																	options={LeadPhoneType.values()}
																	valueExtractor={(option) => option}
																	labelExtractor={(option) => option.label}
																/>
															</Mui.Grid>
															<Mui.Grid item xs={1}>
																<Mui.IconButton
																	size="small"
																	onClick={() => {
																		const leadPhones = [...state.leadPhones];
																		leadPhones[actualIndex].deleted = true;
																		onStateUpdate("leadPhones", leadPhones);
																	}}
																>
																	<Icons.Trash2 size={20} color="#ef4444" />
																</Mui.IconButton>
															</Mui.Grid>
														</Mui.Grid>
													</Mui.Grid>
												);
											})}
										{canAddPhone && state.leadPhones.filter((p) => !p.deleted).length < 3 && (
											<Mui.Grid item>
												<AddAnotherButton
													onClick={() => {
														const leadPhones = [...state.leadPhones];
														leadPhones.push({
															deleted: false,
															value: "",
															type: LeadPhoneType.MOBILE,
															leadId: lead?.id || 0,
														});
														onStateUpdate("leadPhones", leadPhones);
													}}
												/>
											</Mui.Grid>
										)}
									</Mui.Grid>
								</Mui.Grid>

							{/* Street Address */}
							<Mui.Grid item>
								<FormField
									label="Street Address"
									value={state.address}
									onChange={(e) => onStateUpdate("address", e.target.value)}
								/>
							</Mui.Grid>

							{/* City, State, Postal Code */}
							<Mui.Grid item>
								<Mui.Grid container spacing={1}>
									<Mui.Grid item xs={4}>
										<FormField
											label="City"
											value={state.city}
											onChange={(e) => onStateUpdate("city", e.target.value)}
										/>
									</Mui.Grid>
									<Mui.Grid item xs={4}>
										<FormField
											label="State"
											value={state.state}
											onChange={(e) => onStateUpdate("state", e.target.value)}
										/>
									</Mui.Grid>
									<Mui.Grid item xs={4}>
										<FormField
											label="Postal Code"
											value={state.zip}
											onChange={(e) => onStateUpdate("zip", e.target.value)}
										/>
									</Mui.Grid>
								</Mui.Grid>
							</Mui.Grid>

							{/* Birthday */}
							{crmAnniversary && (
								<Mui.Grid item>

										<AppDatePicker
											format="MM/dd"
											fullWidth
											label="Birthday"
											placeholder="mm/dd"
											value={toDateValue(state.birthday && isEdit ? (state.birthday as LeadAnniversary).startDate : state.birthday)}
											onChange={onChangeBirthday}
											openPickerIcon={<Icons.Calendar size={20} />}
											views={["day"]}
											popperPlacement="bottom-end"
											inputLabelClasses={{
													root: classes.fieldLabel,
													asterisk: classes.fieldLabelAsterisk,
												}}
											inputClasses={{
													root: classes.textFieldRoot,
													focused: classes.textFieldFocused,
													notchedOutline: classes.textFieldNotchedOutline,
													input: classes.textFieldInput,
												}}
											endAdornment={state.birthday ? (
													<Mui.InputAdornment position="end">
														<Mui.IconButton
															size="small"
															onClick={() => onStateUpdate("birthday", null)}
															edge="end"
														>
															<Icons.X size={16} />
														</Mui.IconButton>
													</Mui.InputAdornment>
												) : undefined}
										/>

								</Mui.Grid>
							)}

							{/* Transaction Anniversary */}
							{crmAnniversary && (
								<Mui.Grid item>

										<AppDatePicker
											format="MM/dd/yyyy"
											fullWidth
											label="Transaction Anniversary"
											placeholder="mm/dd/yyyy"
											value={toDateValue(state.transactionAnniversary && isEdit ? (state.transactionAnniversary as LeadAnniversary).startDate : state.transactionAnniversary)}
											onChange={onChangeTransactionAnniversary}
											openPickerIcon={<Icons.Calendar size={20} />}
											popperPlacement="bottom-end"
											inputLabelClasses={{
													root: classes.fieldLabel,
													asterisk: classes.fieldLabelAsterisk,
												}}
											inputClasses={{
													root: classes.textFieldRoot,
													focused: classes.textFieldFocused,
													notchedOutline: classes.textFieldNotchedOutline,
													input: classes.textFieldInput,
												}}
											endAdornment={state.transactionAnniversary ? (
													<Mui.InputAdornment position="end">
														<Mui.IconButton
															size="small"
															onClick={() => onStateUpdate("transactionAnniversary", null)}
															edge="end"
														>
															<Icons.X size={16} />
														</Mui.IconButton>
													</Mui.InputAdornment>
												) : undefined}
										/>

								</Mui.Grid>
							)}

							{/* Assigned Agent */}
							{isBroker && isAdmin && (
								<Mui.Grid item>
									<FormSelectField
										label="Assigned Agent"
										value={state.agentId ?? 0}
										onChange={(value) => onStateUpdate("agentId", value)}
										options={[{ id: 0, label: "Unassigned" }, ...Array.from(agents)]}
										valueExtractor={(agent) => agent.id}
										labelExtractor={(agent) => agent.label || agent.name}
									/>
								</Mui.Grid>
							)}

							{/* Divider */}
							<Mui.Grid item>
								<Mui.Divider className={classes.divider} />
							</Mui.Grid>

							{/* Lead Readiness Section */}
							{extendedLeadFields && (
								<>
									<Mui.Grid item>
										<SectionHeader title="Lead Readiness" />
									</Mui.Grid>

									<Mui.Grid item>
										<Mui.Grid container spacing={1}>
											{/* Working with Another Agent */}
											<Mui.Grid item xs={6}>
												<FormSelectField
													label="Working with Another Agent?"
													value={state.workingWithAnotherAgent}
													onChange={(value) =>
														onStateUpdate("workingWithAnotherAgent", value)
													}
													options={YesNoType.values()}
													valueExtractor={(option) => option.value}
													labelExtractor={(option) => option.label}
												/>
											</Mui.Grid>

										</Mui.Grid>
									</Mui.Grid>

									<Mui.Grid item>
										<Mui.Grid container spacing={1}>
											{/* Type */}
											{crmTags && (
												<Mui.Grid item xs={6}>
													<FormSelectField
														label="Type"
														value={state.typeIds || []}
														onChange={(values) => onStateUpdate("typeIds", values)}
														multiple
														options={LeadTagType.leadTypeValues()}
														valueExtractor={(type) => type.id}
														labelExtractor={(type) => type.label}
													/>
												</Mui.Grid>
											)}

											{/* Timeline */}
											<Mui.Grid item xs={6}>
												<FormSelectField
													label="Timeline"
													value={state.timeline}
													onChange={onChangeTimeline}
													options={LeadTimelineType.leadTimelineValues()}
													valueExtractor={(option) => option.value}
													labelExtractor={(option) => option.label}
												/>
											</Mui.Grid>
										</Mui.Grid>
									</Mui.Grid>

									<Mui.Grid item>
										<Mui.Grid container spacing={1}>
											{/* Price */}
											<Mui.Grid item xs={6}>
												<FormField
													label="Price"
													value={formatPriceInput(state.transactionPrice || 0)}
													onChange={(e) => {
														const value = e.target.value.replace(/[^0-9]/g, "");
														onStateUpdate("transactionPrice", parseInt(value) || 0);
													}}
												/>
											</Mui.Grid>

											{/* Lead Quality */}
											<Mui.Grid item xs={6}>
												<FormSelectField
													label="Lead Quality"
													value={state.readiness}
													onChange={(value) => onStateUpdate("readiness", value)}
													options={LeadReadiness.leadReadinessValues()}
													valueExtractor={(option) => option.value}
													labelExtractor={(option) => option.label}
												/>
											</Mui.Grid>
										</Mui.Grid>
									</Mui.Grid>

									<Mui.Grid item>
										<Mui.Grid container spacing={1}>
											{/* Mortgage Pre-approval */}
											<Mui.Grid item xs={6}>
												<FormSelectField
													label="Mortgage Pre-approval"
													value={state.mortgagePreApproval}
													onChange={(value) =>
														onStateUpdate("mortgagePreApproval", value)
													}
													options={YesNoType.values()}
													valueExtractor={(option) => option.value}
													labelExtractor={(option) => option.label}
												/>
											</Mui.Grid>

											{/* Referral Requested */}
											<Mui.Grid item xs={6}>
												<FormSelectField
													label="Referral Requested"
													value={state.referralRequestedFlag}
													onChange={onReferralRequestedChange}
													options={YesNoType.values()}
													valueExtractor={(option) => option.value}
													labelExtractor={(option) => option.label}
												/>
											</Mui.Grid>
										</Mui.Grid>
									</Mui.Grid>
								</>
							)}
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.DialogContent>
			<Mui.DialogActions>
				<Mui.Button
					onClick={handleClose}
					color={"primary"}
					variant="outlined"
				>
					Cancel
				</Mui.Button>
				<Mui.Button
					onClick={() => {
						submittedRef.current = !errors;
						Promise.resolve(handleSubmit(state)).catch((error) => {
							submittedRef.current = false;
							throw error;
						});
					}}
					color="primary"
					variant="contained"
				>
					{isEdit ? "Save" : "Add Lead"}
				</Mui.Button>
			</Mui.DialogActions>
			{open && <UnsavedChangesPrompt isDirty={() => dirty && !submittedRef.current} history={history} />}
			<UnsavedChangesDialog
				open={discardDialogOpen}
				onStay={() => setDiscardDialogOpen(false)}
				onLeave={discardAndClose}
				message="You have unsaved changes. If you close this form, your changes will be lost."
				stayLabel="Keep Editing"
				leaveLabel="Discard Changes"
			/>
		</Dialog>
	);
};

export default withStyles(LeadFormV2Component, styles);
